import React, { useEffect, useState } from 'react';
import PageHeader from './components/PageHeader';
import PlannerView from './components/planner/PlannerView';
import SetupForm from './components/SetupForm';
import {
  CourseFieldKey,
  CourseEntry,
  AcademicSystem,
  Requirement,
  YearPlan,
  MajorRequirement,
  MinorRequirement,
  MultiSelectOption,
  TermPlan,
} from './types';
import {
  buildCoursesForTerm,
  computeMajorSummaries,
  computeRequirementSummaries,
  condensedLabel,
  makeYearPlan,
  activeTermsPerYear,
  termsForSystem,
} from './utils/planner';

const createBlankCourse = (): CourseEntry => ({
  id: `course-${Math.random().toString(16).slice(2, 7)}`,
  name: '',
  distributives: [],
  requiredCourseId: '',
  isMajorRequirement: false,
});

const courseHasName = (course: CourseEntry) => course.name.trim() !== '';

const courseHasContent = (course: CourseEntry) =>
  Boolean(
    courseHasName(course) ||
      course.distributives.length > 0 ||
      course.requiredCourseId ||
      course.isMajorRequirement
  );

const normalizeCourse = (course: CourseEntry): CourseEntry => ({
  ...course,
  name: typeof course.name === 'string' ? course.name : '',
  distributives: Array.isArray(course.distributives) ? course.distributives : [],
  requiredCourseId: course.requiredCourseId ?? '',
  isMajorRequirement:
    (course as any).isMajorRequirement !== undefined
      ? Boolean((course as any).isMajorRequirement)
      : Boolean(course.requiredCourseId),
});

const normalizeTermPlan = (termPlan: TermPlan): TermPlan => {
  const courses = termPlan.courses.map((course) => normalizeCourse(course));
  const legacyCourseCompletion = termPlan.courses.length > 0 && termPlan.courses.every((course) => (course as any).completed);
  const completed =
    typeof termPlan.completed === 'boolean'
      ? termPlan.completed
      : legacyCourseCompletion;

  return {
    ...termPlan,
    completed: Boolean(completed),
    courses,
  };
};

const normalizeYearPlans = (plans: YearPlan[]): YearPlan[] =>
  plans.map((year) => ({
    ...year,
    terms: year.terms.map((term) => normalizeTermPlan(term)),
  }));

const ensureCourseSlots = (courses: CourseEntry[], desiredSlots: number): CourseEntry[] => {
  const safeSlots = Math.max(desiredSlots, 1);
  let normalized = courses.map((course) => normalizeCourse(course));

  if (normalized.length > safeSlots) {
    const extra = normalized.slice(safeSlots);
    const hasContent = extra.some(courseHasContent);
    if (!hasContent) {
      normalized = normalized.slice(0, safeSlots);
    }
  } else if (normalized.length < safeSlots) {
    const additional = Array.from({ length: safeSlots - normalized.length }, () => createBlankCourse());
    normalized = normalized.concat(additional);
  }

  if (normalized.length === 0) {
    return [createBlankCourse()];
  }

  return normalized;
};

const yearHasContent = (year: YearPlan) => year.terms.some((termPlan) => termPlan.courses.some(courseHasContent));

const createInitialRequirements = (): Requirement[] => [{ id: 'req-1', name: '', count: '' }];

const COOKIE_KEY = 'majorBuddyPlanner';
const COOKIE_MAX_AGE_DAYS = 14;
const COOKIE_CHUNK_SIZE = 3200;
const STORAGE_KEY = 'majorBuddyPlannerState';

const getCookie = (name: string): string | null => {
  if (typeof document === 'undefined') return null;
  const escapedName = name.replace(/([.$?*|{}()[\]\\/+^])/g, '\\$1');
  const match = document.cookie.match(new RegExp(`(?:^|; )${escapedName}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
};

const setCookie = (name: string, value: string, days = COOKIE_MAX_AGE_DAYS) => {
  if (typeof document === 'undefined') return;
  const expires = new Date(Date.now() + days * 24 * 60 * 60 * 1000).toUTCString();
  document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/`;
};

const deleteCookie = (name: string) => {
  if (typeof document === 'undefined') return;
  document.cookie = `${name}=; expires=${new Date(0).toUTCString()}; path=/`;
};

const clearCookieChunks = (name: string) => {
  const existingCount = Number(getCookie(`${name}_count`));
  if (Number.isFinite(existingCount) && existingCount > 0) {
    for (let index = 0; index < existingCount; index += 1) {
      deleteCookie(`${name}_${index}`);
    }
  }
  deleteCookie(`${name}_count`);
  deleteCookie(name);
};

const writeCookieValue = (name: string, value: string) => {
  if (typeof document === 'undefined') return;
  clearCookieChunks(name);
  if (value.length <= COOKIE_CHUNK_SIZE) {
    setCookie(name, value);
    setCookie(`${name}_count`, '0');
    return;
  }
  const chunkCount = Math.ceil(value.length / COOKIE_CHUNK_SIZE);
  setCookie(`${name}_count`, chunkCount.toString());
  for (let index = 0; index < chunkCount; index += 1) {
    const chunk = value.slice(index * COOKIE_CHUNK_SIZE, (index + 1) * COOKIE_CHUNK_SIZE);
    setCookie(`${name}_${index}`, chunk);
  }
};

const readCookieValue = (name: string): string | null => {
  const countRaw = getCookie(`${name}_count`);
  if (!countRaw || Number(countRaw) <= 0) {
    return getCookie(name);
  }
  const chunkCount = Number(countRaw);
  if (!Number.isFinite(chunkCount) || chunkCount <= 0) {
    return getCookie(name);
  }
  let result = '';
  for (let index = 0; index < chunkCount; index += 1) {
    const chunk = getCookie(`${name}_${index}`);
    if (chunk === null) return null;
    result += chunk;
  }
  return result;
};

const readPersistedState = (): string | null => {
  if (typeof window !== 'undefined') {
    try {
      const stored = window.localStorage.getItem(STORAGE_KEY);
      if (stored) return stored;
    } catch {
      // Ignore storage errors
    }
  }
  return readCookieValue(COOKIE_KEY);
};

const writePersistedState = (value: string) => {
  if (typeof window !== 'undefined') {
    try {
      window.localStorage.setItem(STORAGE_KEY, value);
    } catch {
      // Ignore quota errors
    }
  }
  writeCookieValue(COOKIE_KEY, value);
};

function App() {
  const [selection, setSelection] = useState<AcademicSystem | null>(null);
  const [plannerVisible, setPlannerVisible] = useState(false);
  const [startYear, setStartYear] = useState('');
  const [classesPerTerm, setClassesPerTerm] = useState('');
  const [classesToGraduate, setClassesToGraduate] = useState('');
  const [requirements, setRequirements] = useState<Requirement[]>(createInitialRequirements);
  const [yearPlans, setYearPlans] = useState<YearPlan[]>([]);
  const [majors, setMajors] = useState<MajorRequirement[]>([]);
  const [minors, setMinors] = useState<MinorRequirement[]>([]);
  const [showDistributives, setShowDistributives] = useState(true);
  const [hydrated, setHydrated] = useState(false);
  const options: Array<{ id: AcademicSystem; title: string; detail: string }> = [
    { id: 'quarter', title: 'Quarters', detail: 'Three concise terms, one pause.' },
    { id: 'semester', title: 'Semesters', detail: 'Two terms, one pause.' },
  ];

  const clearRequiredCourseRefs = (idsToClear: Set<string>) => {
    if (idsToClear.size === 0) return;
    setYearPlans((prev) =>
      prev.map((year) => ({
        ...year,
        terms: year.terms.map((termPlan) => ({
          ...termPlan,
          courses: termPlan.courses.map((course) =>
            course.requiredCourseId && idsToClear.has(course.requiredCourseId)
              ? { ...course, requiredCourseId: '', isMajorRequirement: false, name: '' }
              : course
          ),
        })),
      }))
    );
  };

  useEffect(() => {
    const raw = readPersistedState();
    if (!raw) {
      setHydrated(true);
      return;
    }
    try {
      const saved = JSON.parse(raw);
      if (saved.selection === 'quarter' || saved.selection === 'semester') {
        setSelection(saved.selection);
      }
      if (typeof saved.startYear === 'string') setStartYear(saved.startYear);
      if (typeof saved.classesPerTerm === 'string') setClassesPerTerm(saved.classesPerTerm);
      if (typeof saved.classesToGraduate === 'string') setClassesToGraduate(saved.classesToGraduate);
      if (Array.isArray(saved.requirements) && saved.requirements.length > 0) setRequirements(saved.requirements);
      if (Array.isArray(saved.yearPlans)) setYearPlans(normalizeYearPlans(saved.yearPlans));
      if (Array.isArray(saved.majors)) setMajors(saved.majors);
      if (Array.isArray(saved.minors)) setMinors(saved.minors);
      if (typeof saved.plannerVisible === 'boolean') setPlannerVisible(saved.plannerVisible);
      if (typeof saved.showDistributives === 'boolean') setShowDistributives(saved.showDistributives);
    } catch {
      // Ignore malformed cookies
    } finally {
      setHydrated(true);
    }
  }, []);

  useEffect(() => {
    if (!hydrated) return;
    const payload = {
      selection,
      plannerVisible,
      startYear,
      classesPerTerm,
      classesToGraduate,
      requirements,
      yearPlans,
      majors,
      minors,
      showDistributives,
    };
    try {
      writePersistedState(JSON.stringify(payload));
    } catch {
      // Ignore write failures
    }
  }, [
    selection,
    plannerVisible,
    startYear,
    classesPerTerm,
    classesToGraduate,
    requirements,
    yearPlans,
    majors,
    minors,
    showDistributives,
    hydrated,
  ]);

  const addRequirement = () => {
    setRequirements((prev) => [
      ...prev,
      { id: `req-${prev.length + 1}-${Math.random().toString(16).slice(2)}`, name: '', count: '' },
    ]);
  };

  const updateRequirement = (id: string, field: 'name' | 'count', value: string) => {
    setRequirements((prev) => prev.map((req) => (req.id === id ? { ...req, [field]: value } : req)));
  };

  const removeRequirement = (id: string) => {
    const removedName = requirements.find((req) => req.id === id)?.name;
    setRequirements((prev) => prev.filter((req) => req.id !== id));
    if (removedName?.trim()) {
      setYearPlans((prev) =>
        prev.map((year) => ({
          ...year,
          terms: year.terms.map((termPlan) => ({
            ...termPlan,
            courses: termPlan.courses.map((course) => ({
              ...course,
              distributives: course.distributives.filter((dist) => dist !== removedName),
            })),
          })),
        }))
      );
    }
  };

  const addMajor = () => {
    setMajors((prev) => [
      ...prev,
      {
        id: `major-${prev.length + 1}-${Math.random().toString(16).slice(2, 5)}`,
        name: '',
        classes: [{ id: `maj-class-1-${Math.random().toString(16).slice(2, 5)}`, name: '' }],
      },
    ]);
  };

  const updateMajorName = (majorId: string, value: string) => {
    setMajors((prev) => prev.map((major) => (major.id === majorId ? { ...major, name: value } : major)));
  };

  const updateMajorClass = (majorId: string, classId: string, value: string) => {
    setMajors((prev) =>
      prev.map((major) =>
        major.id === majorId
          ? {
              ...major,
              classes: major.classes.map((cls) => (cls.id === classId ? { ...cls, name: value } : cls)),
            }
          : major
      )
    );
  };

  const addMajorClass = (majorId: string) => {
    setMajors((prev) =>
      prev.map((major) =>
        major.id === majorId
          ? {
              ...major,
              classes: [...major.classes, { id: `maj-class-${major.classes.length + 1}-${Math.random().toString(16).slice(2, 5)}`, name: '' }],
            }
          : major
      )
    );
  };

  const removeMajor = (majorId: string) => {
    const majorToRemove = majors.find((major) => major.id === majorId);
    const idsToClear = new Set(majorToRemove?.classes.map((cls) => cls.id) ?? []);
    setMajors((prev) => prev.filter((major) => major.id !== majorId));
    clearRequiredCourseRefs(idsToClear);
  };

  const removeMajorClass = (majorId: string, classId: string) => {
    setMajors((prev) =>
      prev.map((major) => (major.id === majorId ? { ...major, classes: major.classes.filter((cls) => cls.id !== classId) } : major))
    );
    clearRequiredCourseRefs(new Set([classId]));
  };

  const addMinor = () => {
    setMinors((prev) => [
      ...prev,
      {
        id: `minor-${prev.length + 1}-${Math.random().toString(16).slice(2, 5)}`,
        name: '',
        classes: [{ id: `min-class-1-${Math.random().toString(16).slice(2, 5)}`, name: '' }],
      },
    ]);
  };

  const updateMinorName = (minorId: string, value: string) => {
    setMinors((prev) => prev.map((minor) => (minor.id === minorId ? { ...minor, name: value } : minor)));
  };

  const updateMinorClass = (minorId: string, classId: string, value: string) => {
    setMinors((prev) =>
      prev.map((minor) =>
        minor.id === minorId
          ? {
              ...minor,
              classes: minor.classes.map((cls) => (cls.id === classId ? { ...cls, name: value } : cls)),
            }
          : minor
      )
    );
  };

  const addMinorClass = (minorId: string) => {
    setMinors((prev) =>
      prev.map((minor) =>
        minor.id === minorId
          ? {
              ...minor,
              classes: [...minor.classes, { id: `min-class-${minor.classes.length + 1}-${Math.random().toString(16).slice(2, 5)}`, name: '' }],
            }
          : minor
      )
    );
  };

  const removeMinor = (minorId: string) => {
    const minorToRemove = minors.find((minor) => minor.id === minorId);
    const idsToClear = new Set(minorToRemove?.classes.map((cls) => cls.id) ?? []);
    setMinors((prev) => prev.filter((minor) => minor.id !== minorId));
    clearRequiredCourseRefs(idsToClear);
  };

  const removeMinorClass = (minorId: string, classId: string) => {
    setMinors((prev) =>
      prev.map((minor) => (minor.id === minorId ? { ...minor, classes: minor.classes.filter((cls) => cls.id !== classId) } : minor))
    );
    clearRequiredCourseRefs(new Set([classId]));
  };

  const relabelYears = (plans: YearPlan[]) => {
    const baseYear = Number(startYear);
    const hasStartYear = startYear.trim() !== '' && Number.isFinite(baseYear);
    return plans.map((year, index) => ({
      ...year,
      label: hasStartYear ? condensedLabel(baseYear, index) : `Year ${index + 1}`,
    }));
  };

  const ensurePlanner = () => {
    if (!selection) return;
    const terms = termsForSystem(selection);
    const courseTermsPerYear = activeTermsPerYear(selection);
    const numericPerTerm = Number(classesPerTerm);
    const courseSlotsPerTerm =
      Number.isFinite(numericPerTerm) && numericPerTerm > 0 ? Math.floor(numericPerTerm) : 1;
    const totalClassesNumeric = Number(classesToGraduate);
    const hasTotalClasses =
      classesToGraduate.trim() !== '' && Number.isFinite(totalClassesNumeric) && totalClassesNumeric > 0;
    const classesPerYear = courseSlotsPerTerm * Math.max(courseTermsPerYear, 1);
    const computedYearCount =
      hasTotalClasses && classesPerYear > 0 ? Math.ceil(totalClassesNumeric / classesPerYear) : null;
    const fallbackYearCount = yearPlans.length || 4;
    const desiredYearCount = Math.max(computedYearCount ?? fallbackYearCount, 1);

    setYearPlans((prev) => {
      const canTrimYears = !prev.slice(desiredYearCount).some(yearHasContent);
      const basePlans = canTrimYears ? prev.slice(0, desiredYearCount) : prev;

      let updated = basePlans.map((year) => ({
        ...year,
        terms: terms.map((term) => {
          const existing = year.terms.find((t) => t.term === term);
          const courses = ensureCourseSlots(existing ? existing.courses : [], courseSlotsPerTerm);
          return { term, completed: existing?.completed ?? false, courses };
        }),
      }));

      while (updated.length < desiredYearCount) {
        const newYear = makeYearPlan(updated.length + 1, terms);
        updated.push({
          ...newYear,
          terms: newYear.terms.map((termPlan) => ({
            ...termPlan,
            courses: ensureCourseSlots(termPlan.courses, courseSlotsPerTerm),
          })),
        });
      }

      return relabelYears(updated);
    });

    setPlannerVisible(true);
  };

  const addYear = () => {
    if (!selection) return;
    const terms = termsForSystem(selection);
    setYearPlans((prev) => {
      const updated = [
        ...prev,
        {
          ...makeYearPlan(prev.length + 1, terms),
          terms: terms.map((term) => ({ term, completed: false, courses: buildCoursesForTerm(classesPerTerm) })),
        },
      ];
      return relabelYears(updated);
    });
  };

  const removeYear = (yearId: string) => {
    setYearPlans((prev) => relabelYears(prev.filter((year) => year.id !== yearId)));
  };

  const goBackToSetup = () => {
    setPlannerVisible(false);
  };

  const resetPlanner = () => {
    const confirmed = window.confirm(
      'Reset your plan and start from scratch? This clears your years, courses, requirements, majors, and minors.'
    );
    if (!confirmed) return;

    setSelection(null);
    setPlannerVisible(false);
    setStartYear('');
    setClassesPerTerm('');
    setClassesToGraduate('');
    setRequirements(createInitialRequirements());
    setYearPlans([]);
    setMajors([]);
    setMinors([]);
    setShowDistributives(true);

    try {
      if (typeof window !== 'undefined') {
        window.localStorage.removeItem(STORAGE_KEY);
      }
    } catch {
      // Ignore storage errors
    }
    clearCookieChunks(COOKIE_KEY);
  };

  const addCourse = (yearId: string, term: string) => {
    setYearPlans((prev) =>
      prev.map((year) =>
        year.id === yearId
          ? {
              ...year,
              terms: year.terms.map((t) =>
                t.term === term
                  ? {
                      ...t,
                      courses: [
                        ...t.courses,
                        {
                          id: `course-${t.courses.length + 1}-${Math.random().toString(16).slice(2, 5)}`,
                          name: '',
                          distributives: [],
                          requiredCourseId: '',
                          isMajorRequirement: false,
                        },
                      ],
                    }
                  : t
              ),
            }
          : year
      )
    );
  };

  const removeCourse = (yearId: string, term: string, courseId: string) => {
    setYearPlans((prev) =>
      prev.map((year) =>
        year.id === yearId
          ? {
              ...year,
              terms: year.terms.map((t) => (t.term === term ? { ...t, courses: t.courses.filter((course) => course.id !== courseId) } : t)),
            }
          : year
      )
    );
  };

  const updateCourse = (
    yearId: string,
    term: string,
    courseId: string,
    field: CourseFieldKey,
    value: CourseEntry[CourseFieldKey]
  ) => {
    setYearPlans((prev) =>
      prev.map((year) =>
        year.id === yearId
          ? {
              ...year,
              terms: year.terms.map((t) =>
                t.term === term
                  ? {
                      ...t,
                      courses: t.courses.map((course) => (course.id === courseId ? { ...course, [field]: value } : course)),
                    }
                  : t
              ),
            }
          : year
      )
    );
  };

  const updateTermCompletion = (yearId: string, term: string, completed: boolean) => {
    setYearPlans((prev) =>
      prev.map((year) =>
        year.id === yearId
          ? {
              ...year,
              terms: year.terms.map((t) => (t.term === term ? { ...t, completed } : t)),
            }
          : year
      )
    );
  };

  const completedCoursesCount = yearPlans.reduce(
    (sum, year) =>
      sum +
      year.terms.reduce(
        (inner, term) => inner + (term.completed ? term.courses.filter(courseHasName).length : 0),
        0
      ),
    0
  );
  const totalClassRequirement = Number(classesToGraduate);
  const hasClassRequirement = classesToGraduate.trim() !== '' && Number.isFinite(totalClassRequirement);
  const totalClassesRemaining = hasClassRequirement ? Math.max(totalClassRequirement - completedCoursesCount, 0) : null;
  const buildProgramClassLookup = (programs: Array<{ name: string; classes: { id: string; name: string }[] }>) =>
    programs.reduce<Record<string, { major: string; name: string }>>((acc, program) => {
      program.classes.forEach((cls) => {
        if (cls.name.trim()) {
          acc[cls.id] = { major: program.name, name: cls.name };
        }
      });
      return acc;
    }, {});
  const majorClassLookup = buildProgramClassLookup(majors);
  const minorClassLookup = buildProgramClassLookup(minors);

  const requirementSummaries = computeRequirementSummaries(yearPlans, requirements);
  const majorSummaries = computeMajorSummaries(yearPlans, majors, majorClassLookup, 'Major');
  const minorSummaries = computeMajorSummaries(yearPlans, minors, minorClassLookup, 'Minor');

  const distributiveOptions: MultiSelectOption[] = requirements
    .filter((req) => req.name.trim() !== '')
    .map((req) => ({ value: req.name, label: req.name }));

  return (
    <div className="min-h-screen bg-white text-slate-900 antialiased">
      <div className="mx-auto flex min-h-screen max-w-7xl flex-col px-6 py-12 md:px-12 lg:px-16">
        <PageHeader />

        {!plannerVisible && (
          <main className="flex-1">
            <SetupForm
              options={options}
              selection={selection}
              startYear={startYear}
              classesPerTerm={classesPerTerm}
              classesToGraduate={classesToGraduate}
              onSelectionChange={setSelection}
              onStartYearChange={setStartYear}
              onClassesPerTermChange={setClassesPerTerm}
              onClassesToGraduateChange={setClassesToGraduate}
              onAddRequirement={addRequirement}
              onUpdateRequirement={updateRequirement}
              onRemoveRequirement={removeRequirement}
              onContinue={ensurePlanner}
            />
          </main>
        )}

        {plannerVisible && selection && (
          <main className="flex-1">
            <PlannerView
              selection={selection}
              yearPlans={yearPlans}
              requirements={requirements}
              majors={majors}
              minors={minors}
              showDistributives={showDistributives}
              distributiveOptions={distributiveOptions}
              requirementSummaries={requirementSummaries}
              majorSummaries={majorSummaries}
              minorSummaries={minorSummaries}
              hasClassRequirement={hasClassRequirement}
              totalClassesRemaining={totalClassesRemaining}
              totalClassRequirement={totalClassRequirement}
              onAddYear={addYear}
              onRemoveYear={removeYear}
              onGoBack={goBackToSetup}
              onAddCourse={addCourse}
              onRemoveCourse={removeCourse}
              onUpdateCourse={updateCourse}
              onUpdateTermCompletion={updateTermCompletion}
              onToggleDistributives={setShowDistributives}
              onAddRequirement={addRequirement}
              onUpdateRequirement={updateRequirement}
              onRemoveRequirement={removeRequirement}
              onAddMajor={addMajor}
              onUpdateMajorName={updateMajorName}
              onUpdateMajorClass={updateMajorClass}
              onAddMajorClass={addMajorClass}
              onRemoveMajor={removeMajor}
              onRemoveMajorClass={removeMajorClass}
              onAddMinor={addMinor}
              onUpdateMinorName={updateMinorName}
              onUpdateMinorClass={updateMinorClass}
              onAddMinorClass={addMinorClass}
              onRemoveMinor={removeMinor}
              onRemoveMinorClass={removeMinorClass}
              onResetPlan={resetPlanner}
            />
          </main>
        )}

        <footer className="mt-12 text-center text-xs text-slate-500">
          by{' '}
          <a
            className="text-slate-500 underline-offset-2 hover:text-slate-700 hover:underline"
            href="https://github.com/aiexr"
            target="_blank"
            rel="noreferrer"
          >
            aiexr
          </a>{' '}
          and{' '}
          <a
            className="text-slate-500 underline-offset-2 hover:text-slate-700 hover:underline"
            href="https://github.com/benitolinito"
            target="_blank"
            rel="noreferrer"
          >
            benitolinito
          </a>
        </footer>
      </div>
    </div>
  );
}

export default App;
